import React from "react";
import { useSelector } from "react-redux";
import { useProductAttributes } from "../Hook/useQuoteOptions";
import "./Quotes.scss";

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

// variant_json is either { [childId]: {...} } or { variant: { [childId]: {...} }, oem, ... }
const getVariantMap = (variantJson) => {
  const parsed = parseJson(variantJson);
  if (!parsed) return {};
  return parsed.variant && typeof parsed.variant === "object"
    ? parsed.variant
    : parsed;
};

const toLabel = (value, lov = []) => {
  if (value === undefined || value === null || value === "") return null;
  const match = lov.find(
    (item) => String(item.value) === String(value) || item.name === value
  );
  if (match) return match.name || match.label || match.value;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

// Full rupee amount, e.g. ₹4,679
const formatPremium = (amount) =>
  amount
    ? Number(amount).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      })
    : "-";

// Per-product premium rows: premium_breakup from Get Quote, or the saved
// quote's products (getQuote fills each one's premium from the payment split)
const getPremiumBreakup = (version) => {
  if (Array.isArray(version.premium_breakup)) return version.premium_breakup;
  if (Array.isArray(version.products)) {
    return version.products
      .filter((product) => product.premium)
      .map((product) => ({
        product_name: product.product_name,
        premium: product.premium,
      }));
  }
  return [];
};

const Quotes = () => {
  const detailsData = useSelector(
    (state) => state.order.selectedOrders.detailsData
  );
  const productAttributeData = useSelector(
    (state) => state.order.productAttributeData
  );
  const productVariant = useSelector(
    (state) => state.order.selectedOrders.productVariant
  );
  const formState = useSelector((state) => state.common.formRendererState);
  const productVariantModalState = useSelector(
    (state) => state.order.productVariantModalState
  );
  const userInfo = useSelector((state) => state.common.userInfo);

  useProductAttributes(detailsData.product_id || formState.product_id);

  // Insurer name and per-product premiums from the Get Quote response held in store
  const getInsurerFromStore = (oemCode) => {
    const product = productVariant?.content?.find((item) =>
      item.product_variants?.some((variant) => variant.pspOem?.code === oemCode)
    );
    if (!product) return {};
    const variants = product.product_variants.filter(
      (variant) => variant.pspOem?.code === oemCode
    );
    return {
      oem_name: variants[0]?.pspOem?.name,
      premium_breakup: variants
        .map((variant) => ({
          product_name: variant.name || variant.product_name,
          premium: variant.final_premium || variant.premium_amount,
        }))
        .filter((item) => item.product_name && item.premium),
    };
  };

  // Saved versions when the backend provides them, otherwise the quote itself,
  // otherwise the selection just made in the attributes modal (not saved yet).
  const versions = Array.isArray(detailsData.quote_versions)
    ? detailsData.quote_versions
    : detailsData.entityid ?? detailsData.entity_id
    ? [detailsData]
    : productVariantModalState?.oem
    ? [
        {
          variant_json: productVariantModalState.variant,
          oem: productVariantModalState.oem,
          gross_amount: productVariantModalState.finalPremium,
          created_date: new Date().toLocaleString(),
          created_by_name: userInfo?.name,
          ...getInsurerFromStore(productVariantModalState.oem),
        },
      ]
    : [];

  if (!versions.length) {
    return <div className="text-muted">No quote generated yet.</div>;
  }

  return (
    <div>
      {versions.map((version, index) => {
        const variantMap = getVariantMap(version.variant_json);
        const versionNo = version.version || versions.length - index;
        const isCurrent =
          version.is_current !== undefined
            ? Number(version.is_current) === 1
            : index === 0;
        const children = (productAttributeData || []).filter(
          (child) => variantMap[child.entityid]
        );
        const premiumBreakup = getPremiumBreakup(version);
        const addonRows = children
          .map((child) => ({
            child,
            addons: (child.product_addon_list || []).filter(
              (addon) => variantMap[child.entityid]?.[addon.name]
            ),
          }))
          .filter((row) => row.addons.length);

        return (
          <div key={version.entityid || index} className="quote-version">
            <div className="quote-version--header">
              <div className="quote-version--title">
                Quote V{versionNo}
                {isCurrent && (
                  <span className="quote-version--current">● Current</span>
                )}
              </div>
              <span className="quote-version--generated">
                Generated {version.created_date || "-"}
                {version.created_by_name || version.rm_name
                  ? ` by ${version.created_by_name || version.rm_name}`
                  : ""}
              </span>
            </div>

            <div className="quote-version--body">
              <div className="quote-version--section">Attributes</div>
              {children.map((child) => {
                const values = variantMap[child.entityid] || {};
                const chips = (child.product_attributes_list || [])
                  .map((attribute) => ({
                    key: attribute.attribute_key,
                    label: attribute.name,
                    value: toLabel(
                      values[attribute.attribute_key],
                      attribute.product_attribute_lov
                    ),
                  }))
                  .filter((chip) => chip.value);
                if (!chips.length) return null;
                return (
                  <div key={`attr-${child.entityid}`} className="quote-version--row">
                    <div className="quote-version--product">{child.name}</div>
                    <div className="quote-version--chips">
                      {chips.map((chip) => (
                        <span key={chip.key} className="quote-version--chip">
                          {chip.label}: <strong>{chip.value}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}

              {addonRows.length > 0 && (
                <>
                  <div className="quote-version--section mt-3">Add Ons</div>
                  {addonRows.map(({ child, addons }) => (
                    <div key={`addon-${child.entityid}`} className="quote-version--row">
                      <div className="quote-version--product">{child.name}</div>
                      <div className="quote-version--chips">
                        {addons.map((addon) => (
                          <span
                            key={addon.entityid || addon.name}
                            className="quote-version--chip"
                          >
                            <strong>{addon.label}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div className="quote-version--insurer">
                <i className="ri-check-line quote-version--check"></i>
                <div>
                  <div className="quote-version--label">Insurance Company</div>
                  <div className="quote-version--insurer-name">
                    {version.oem_name || version.oem || "-"}
                  </div>
                </div>
                {premiumBreakup.map((item) => (
                  <div key={item.product_name}>
                    <div className="quote-version--label">{item.product_name}</div>
                    <div className="quote-version--value">
                      {formatPremium(item.premium)}
                    </div>
                  </div>
                ))}
                <div>
                  <div className="quote-version--label">Total Premium</div>
                  <div className="quote-version--value">
                    {formatPremium(version.gross_amount)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Quotes;
